import { useEffect, useMemo, useRef, useState } from "react";
import { KeyRound, ShieldAlert, ShieldCheck } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import AppInformationLinks from "@/components/AppInformationLinks";

function signInError(error) {
  const detail = error.response?.data?.detail;
  if (typeof detail === "string") {
    return detail;
  }
  if (Array.isArray(detail)) {
    return detail
      .map((item) => item?.msg || "Invalid sign-in details")
      .join(" ");
  }
  if (detail && typeof detail === "object" && typeof detail.message === "string") {
    return detail.message;
  }
  if (!error.response) {
    return "The sign-in service could not be reached. Please try again.";
  }
  return "Could not sign in.";
}

function parseLockout(error) {
  if (error.response?.status !== 429) return null;
  const detail = error.response?.data?.detail;
  const retryHeader = Number(error.response?.headers?.["retry-after"]);
  let seconds = null;
  if (detail && typeof detail === "object" && Number.isFinite(detail.retry_after_seconds)) {
    seconds = Math.max(1, Math.floor(detail.retry_after_seconds));
  } else if (Number.isFinite(retryHeader) && retryHeader > 0) {
    seconds = Math.floor(retryHeader);
  } else {
    seconds = 15 * 60;
  }
  const unlocksAt = Date.now() + seconds * 1000;
  return { unlocksAt };
}

function formatRemaining(secondsLeft) {
  if (secondsLeft <= 0) return "any moment now";
  if (secondsLeft < 60) return `${secondsLeft} second${secondsLeft === 1 ? "" : "s"}`;
  const minutes = Math.ceil(secondsLeft / 60);
  return `${minutes} minute${minutes === 1 ? "" : "s"}`;
}

export default function LoginPage() {
  const { login, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hasSubmittedLogin, setHasSubmittedLogin] = useState(false);
  const [lockout, setLockout] = useState(null); // { unlocksAt }
  const [now, setNow] = useState(() => Date.now());
  const tickRef = useRef(null);
  const destination = location.state?.from || "/dashboard";

  useEffect(() => {
    if (!hasSubmittedLogin || !user) {
      return;
    }
    const destinationAfterLogin = user.must_change_password
      ? "/change-password"
      : destination === "/change-password"
        ? "/dashboard"
        : destination;
    navigate(
      !user.must_change_password && user.platform_role === "platform_owner"
        ? "/platform/companies"
        : destinationAfterLogin,
      { replace: true },
    );
  }, [destination, hasSubmittedLogin, navigate, user]);

  useEffect(() => {
    if (!lockout) {
      if (tickRef.current) {
        window.clearInterval(tickRef.current);
        tickRef.current = null;
      }
      return undefined;
    }
    setNow(Date.now());
    tickRef.current = window.setInterval(() => setNow(Date.now()), 1000);
    return () => {
      if (tickRef.current) window.clearInterval(tickRef.current);
      tickRef.current = null;
    };
  }, [lockout]);

  const secondsLeft = useMemo(() => {
    if (!lockout) return 0;
    return Math.max(0, Math.ceil((lockout.unlocksAt - now) / 1000));
  }, [lockout, now]);

  useEffect(() => {
    if (lockout && secondsLeft === 0) {
      setLockout(null);
    }
  }, [lockout, secondsLeft]);

  const submit = async (event) => {
    event.preventDefault();
    if (secondsLeft > 0) return;
    setSubmitting(true);
    try {
      await login(email, password);
      setHasSubmittedLogin(true);
    } catch (error) {
      const lock = parseLockout(error);
      if (lock) {
        setLockout(lock);
      } else {
        toast.error(signInError(error));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="login-page" data-testid="login-page">
      <section className="login-brand-panel">
        <div className="login-brand-mark">J</div>
        <p className="operations-kicker">JFM Operations</p>
        <h1>Sign in to the operating system.</h1>
        <p>
          Project, field, and billing access is protected by your assigned role.
        </p>
      </section>
      <section className="login-form-panel">
        <form data-testid="login-form" onSubmit={submit}>
          <div className="login-form-heading">
            <ShieldCheck size={20} />
            <div>
              <p className="operations-kicker">Secure access</p>
              <h2>Welcome back</h2>
            </div>
          </div>
          {lockout && secondsLeft > 0 && (
            <div
              role="alert"
              data-testid="login-lockout-banner"
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                color: "#991b1b",
                borderRadius: "10px",
                padding: "0.85rem 1rem",
                marginBottom: "1rem",
                display: "flex",
                alignItems: "flex-start",
                gap: "0.6rem",
              }}
            >
              <ShieldAlert size={18} style={{ flexShrink: 0, marginTop: "2px" }} />
              <div style={{ fontSize: "0.85rem", lineHeight: 1.4 }}>
                <div style={{ fontWeight: 600 }}>You&rsquo;re locked out.</div>
                <div>
                  Too many failed attempts. Try again in{" "}
                  <span data-testid="login-lockout-countdown" style={{ fontWeight: 600 }}>
                    {formatRemaining(secondsLeft)}
                  </span>
                  .
                </div>
              </div>
            </div>
          )}
          <label htmlFor="login-email">
            Email
            <input
              autoComplete="email"
              data-testid="login-email-input"
              disabled={secondsLeft > 0}
              id="login-email"
              onChange={(event) => setEmail(event.target.value)}
              required
              type="email"
              value={email}
            />
          </label>
          <label htmlFor="login-password">
            Password
            <input
              autoComplete="current-password"
              data-testid="login-password-input"
              disabled={secondsLeft > 0}
              id="login-password"
              onChange={(event) => setPassword(event.target.value)}
              required
              type="password"
              value={password}
            />
          </label>
          <button
            data-testid="login-submit-button"
            disabled={submitting || secondsLeft > 0}
            type="submit"
          >
            <KeyRound size={16} />
            {secondsLeft > 0
              ? `Try again in ${formatRemaining(secondsLeft)}`
              : submitting
                ? "Signing in…"
                : "Sign in"}
          </button>
          <button
            data-testid="platform-owner-recovery-link"
            onClick={() => navigate("/recover-platform-owner")}
            type="button"
          >
            Platform Owner: use a recovery code
          </button>
          <AppInformationLinks />
        </form>
      </section>

    </main>
  );
}


